import React, { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { useMeta } from "@/hooks/useMeta";
import useKalkulatorCatalog from "@/hooks/useKalkulatorCatalog";
import { Printer, ArrowLeft } from "lucide-react";
import DocumentPreviewContent from "@/components/DocumentPreviewContent";

const SHEET_WIDTH = 794; // matches DocumentPreviewContent's max-w-[794px] (A4 @ ~96dpi)

export default function DocumentPrint() {
  const { docId } = useParams();
  const navigate = useNavigate();
  const { data: d, isLoading } = useQuery({ queryKey: ["doc", docId], queryFn: async () => (await api.get(`/documents/${docId}`)).data });
  const { quotationTemplate } = useMeta();
  // This page is always opened as a brand-new tab (see the Kembali handler below), so the
  // module-level CAT/MEK2_TIERS/LANTAI_CAT in kalkulatorCatalog.js are still the hardcoded
  // defaults until this fetch applies the admin's live catalog over them — without waiting
  // for it here, a kalkulator_estimasi document renders with stale class names/prices baked
  // into the JS bundle instead of whatever's actually configured in Pengaturan right now.
  const { isLoading: catalogLoading } = useKalkulatorCatalog();

  // On screens narrower than the document's own fixed A4 width, scale the whole sheet
  // down to fit — otherwise it's unreadable without scrolling sideways for every line.
  // Print output is untouched: the transform is reset back to none inside @media print,
  // so what gets printed/saved as PDF is always full, real A4 size.
  const innerRef = useRef(null);
  const [scale, setScale] = useState(1);
  const [naturalHeight, setNaturalHeight] = useState(0);

  useEffect(() => {
    const computeScale = () => {
      const available = window.innerWidth - 24; // small side breathing room
      setScale(Math.min(1, available / SHEET_WIDTH));
    };
    computeScale();
    window.addEventListener("resize", computeScale);
    return () => window.removeEventListener("resize", computeScale);
  }, []);

  useEffect(() => {
    if (!innerRef.current) return;
    const el = innerRef.current;
    const measure = () => setNaturalHeight(el.scrollHeight);
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [d]);

  // Paraf mark, attempt #4. Diagnosed attempt #3 directly: the generated SVG data URI is
  // valid (confirmed by rendering it in a plain on-screen <img>) — the mark was vanishing
  // specifically because it was a CSS `background-image`, and Chrome's print pipeline
  // suppresses ALL background-images/colors by default unless the user manually ticks
  // "Background graphics" in the print dialog (a narrow, well-documented toggle that only
  // touches CSS backgrounds — real foreground content is never affected by it). Switching
  // to an actual <img> element sidesteps that toggle entirely. Combined with position:fixed
  // + being a sibling outside the scaled/transformed wrapper (so it isn't mispositioned by
  // that ancestor's transform) + the reserved page-bottom margin below (so body text can
  // physically never reach that zone even if per-page repetition itself is inconsistent).
  const docType = d?.type;
  const parafSrc = React.useMemo(() => {
    const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="260" height="90" viewBox="0 0 260 90">`
      + `<rect x="1" y="1" width="258" height="88" fill="white" stroke="#aaaaaa" stroke-width="2"/>`
      + `<line x1="130" y1="1" x2="130" y2="89" stroke="#aaaaaa" stroke-width="2"/>`
      + `<text x="65" y="20" font-family="Arial, sans-serif" font-size="17" fill="#888888" text-anchor="middle">Paraf I</text>`
      + `<text x="195" y="20" font-family="Arial, sans-serif" font-size="17" fill="#888888" text-anchor="middle">Paraf II</text>`
      + `</svg>`;
    return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
  }, []);

  if (isLoading || !d || !quotationTemplate || (d.type === "kalkulator_estimasi" && catalogLoading)) return <div className="min-h-screen bg-white" />;

  return (
    <div className="print-page min-h-screen bg-zinc-200 py-6">
      <style>{`
        @media print {
          html { color-scheme: light!important; }
          /* background-color, not the plain "background" shorthand: the shorthand resets
             background-image to none as part of setting it, which was silently erasing
             the paraf mark's background-image below even though that rule has higher
             selector specificity (body.kp-paraf-bg vs body). Longhand avoids the clash
             entirely instead of relying on cascade order to resolve it. */
          html, body, #root { background-color:#fff!important; margin:0!important; }
          * { box-shadow:none!important; text-shadow:none!important; outline:none!important; }
          .no-print{display:none!important}
          .print-page{background:#fff!important;padding:0!important;min-height:0!important}
          .doc-scale-outer{height:auto!important;width:100%!important}
          .doc-scale-inner{transform:none!important;width:100%!important}
          .sheet{border:none!important;outline:none!important;margin:0!important;width:100%!important;max-width:100%!important}
          .page-break{page-break-after:always}
          tr{page-break-inside:avoid}
          .kp-paraf-img{
            display:block!important; position:fixed; bottom:2mm; right:8mm;
            width:26mm; height:9mm;
          }
        }
        .kp-paraf-img{display:none}
        @page{size:A4;margin:${d.type === "kontrak_produksi" ? "10mm 10mm 16mm 10mm" : "10mm"}}
      `}</style>
      <div className="no-print max-w-[794px] mx-auto mb-3 flex justify-between px-2" style={{ paddingTop: "env(safe-area-inset-top)" }}>
        <button onClick={() => {
          // This page is always opened as a fresh browser tab (target="_blank" from the
          // print icon on any board — Pipeline, Piutang, Leads, wherever), never as an
          // in-app route the user navigated to directly. So "Kembali" should always mean
          // "close this tab", returning to whatever board/tab was underneath the whole
          // time — that used to fall back to a hardcoded /leads instead, because the link
          // has rel="noreferrer" (needed for the referrer-stripping part), which as a side
          // effect also clears window.opener, so the old opener-based check always failed.
          // window.close() works independently of window.opener for a script/user-opened
          // tab, so this is used unconditionally now, with the old logic only as a fallback
          // for the rare case a browser blocks the close (e.g. tab opened directly by URL).
          window.close();
          setTimeout(() => {
            if (window.history.length > 1) navigate(-1);
            else navigate("/leads");
          }, 150);
        }} className="flex items-center gap-1.5 text-sm text-zinc-700 min-h-[44px] px-1"><ArrowLeft size={16} />Kembali</button>
        <button data-testid="doc-print-btn" onClick={() => window.print()} className="flex items-center gap-1.5 bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-medium min-h-[44px]"><Printer size={16} />Cetak / Simpan PDF</button>
      </div>

      <div className="doc-scale-outer" style={{ width: SHEET_WIDTH * scale, height: naturalHeight ? naturalHeight * scale : undefined, margin: "0 auto", overflow: "hidden" }}>
        <div ref={innerRef} className="doc-scale-inner" style={{ width: SHEET_WIDTH, transform: `scale(${scale})`, transformOrigin: "top left" }}>
          <DocumentPreviewContent d={d} tpl={quotationTemplate} />
        </div>
      </div>

      {docType === "kontrak_produksi" && <img src={parafSrc} alt="" className="kp-paraf-img" />}
    </div>
  );
}
